import { useCallback, useMemo, useState } from 'react'
import { useQuery, useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { MapIcon, PlusIcon, ShapesIcon, TableIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import { ToggleGroup, ToggleGroupItem } from '@ems/ui/components/toggle-group'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import {
  boothTone,
  summarizeFloor,
  type Booth,
  type BoothStatus,
  type FloorSummary,
} from '#admin/features/exhibitor/api.ts'
import { BoothDialog } from '#admin/features/exhibitor/booth-dialog.tsx'
import { BoothTypesDialog } from '#admin/features/exhibitor/booth-types-dialog.tsx'
import { InteractiveFloorPlan } from '#admin/features/exhibitor/floor-plan/interactive-floor-plan.tsx'
import { resolveVenue } from '#admin/features/exhibitor/floor-plan/venues.ts'
import { useSetBoothStatus } from '#admin/features/exhibitor/mutations.ts'
import {
  applicationsQuery,
  boothsQuery,
  boothTypesQuery,
  venueQuery,
} from '#admin/features/exhibitor/queries.ts'
import { StandsTable } from '#admin/features/exhibitor/stands-table.tsx'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { label } from '#admin/features/registrations/api.ts'
import { FloorSkeleton } from '#admin/features/exhibitor/exhibitor-skeletons.tsx'

/**
 * The hall, in two views: the floor plan, drawn (the default), and the tables that count it.
 *
 * The drawing answers "where is the free space and who is next to whom". The tables answer the
 * question an organizer asks every morning, "how much of the floor is sold", and that is a
 * `group by` rather than a canvas. The view is in the address (`?view=table`), so it survives a
 * reload and a link.
 *
 * **`confirmed` is the number that matters**, and it is the one the whole of invariant 02 is
 * about: a booth that is paid for but still shows as `reserved` is floor space the map will
 * offer to somebody else. If that count ever looks low against the paid applications, this is
 * the page where it shows.
 */
export const Route = createFileRoute('/_authenticated/exhibition/floor')({
  pendingComponent: FloorSkeleton,
  validateSearch: (
    search: Record<string, unknown>,
  ): { view?: 'table' | undefined; booth?: string | undefined } => ({
    view: search['view'] === 'table' ? 'table' : undefined,
    // `?booth=A-08` opens the plan on that stand, as v1's mapper link did.
    booth:
      typeof search['booth'] === 'string' && search['booth'] !== '' ? search['booth'] : undefined,
  }),
  staticData: { crumb: { parents: [{ label: 'Exhibition' }], label: 'Floor' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(boothsQuery()),
      context.queryClient.ensureQueryData(boothTypesQuery()),
      // The company on each held stand, for the drawn plan.
      context.queryClient.ensureQueryData(applicationsQuery()),
      // The venue under the plan, so the plan does not draw the default venue first.
      context.queryClient.ensureQueryData(venueQuery()),
    ]),
  component: FloorPage,
})

/** What each status means to somebody selling floor space. */
const MEANING: Record<BoothStatus, string> = {
  available: 'On the market',
  selected: 'Held in a draft application',
  reserved: 'Held by a submitted application, unpaid',
  confirmed: 'Paid for',
  canceled: 'Withdrawn from the floor plan',
}

/** Which dialog is open. One at a time. */
type Dialog =
  | { kind: 'booth'; booth: Booth | null }
  | { kind: 'types' }
  | { kind: 'withdraw'; booth: Booth }
  | null

function FloorPage() {
  const { data } = useSuspenseQuery(boothsQuery())
  const { data: types } = useSuspenseQuery(boothTypesQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useQuery(manifestQuery())
  const { data: saved } = useSuspenseQuery(venueQuery())
  const slug = manifest?.tenant.slug
  // Once per change, so the plan sees one venue object until the venue or the edition changes.
  const venue = useMemo(() => resolveVenue(slug, saved.venue), [slug, saved])
  // A view-only role sees the floor and none of the controls. The server refuses them anyway.
  const canManage = can(session, 'exhibitor.manage')
  const currency = manifest?.tenant.currencyCode ?? 'IDR'
  const [dialog, setDialog] = useState<Dialog>(null)
  const close = () => setDialog(null)
  const setStatus = useSetBoothStatus()
  const search = Route.useSearch()
  const view = search.view ?? 'plan'
  const navigate = useNavigate({ from: Route.fullPath })

  const onEdit = useCallback((booth: Booth) => setDialog({ kind: 'booth', booth }), [])
  const onMove = useCallback(
    (booth: Booth, move: 'withdraw' | 'restore') => {
      // Withdrawing takes a stand off sale, so it is confirmed. Putting it back is not.
      if (move === 'withdraw') setDialog({ kind: 'withdraw', booth })
      else setStatus.mutate({ id: booth.id, status: 'available' })
    },
    [setStatus],
  )
  const summary = summarizeFloor(data.items)
  const sellable = data.items.filter((booth) => booth.status !== 'canceled')
  const sold = data.items.filter((booth) => booth.status === 'confirmed')

  const columns = useMemo<ColumnDef<FloorSummary, never>[]>(
    () => [
      {
        accessorKey: 'status',
        header: 'Status',
        meta: { primary: true },
        cell: ({ row }) => (
          <StatusBadge tone={boothTone(row.original.status)}>
            {label(row.original.status)}
          </StatusBadge>
        ),
      },
      {
        id: 'means',
        header: 'Means',
        cell: ({ row }) => (
          <span className="text-muted-foreground">{MEANING[row.original.status]}</span>
        ),
      },
      { accessorKey: 'count', header: 'Stands', meta: { align: 'end' } },
      {
        id: 'area',
        header: 'Area',
        meta: { align: 'end' },
        // Square meters, not money. Summing a dimension is safe; summing the rates would be this
        // console computing a figure the pricing endpoint also computes.
        cell: ({ row }) => `${row.original.areaSqm.toLocaleString()} m²`,
      },
    ],
    [],
  )

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Floor"
        description={`${sold.length} of ${sellable.length} stands paid for`}
        actions={
          <>
            <ToggleGroup
              type="single"
              variant="outline"
              value={view}
              onValueChange={(next) => {
                // A click on the pressed item clears a single toggle group. Keep a view.
                if (next === 'plan' || next === 'table') {
                  void navigate({
                    search: next === 'table' ? { view: 'table' } : {},
                    replace: true,
                  })
                }
              }}
              aria-label="View"
            >
              <ToggleGroupItem value="plan" className="gap-1.5 px-3">
                <MapIcon />
                Floor plan
              </ToggleGroupItem>
              <ToggleGroupItem value="table" className="gap-1.5 px-3">
                <TableIcon />
                Table
              </ToggleGroupItem>
            </ToggleGroup>
            {canManage ? (
              <>
                <Button variant="outline" onClick={() => setDialog({ kind: 'types' })}>
                  <ShapesIcon />
                  Booth types
                </Button>
                <Button
                  disabled={types.items.length === 0}
                  title={types.items.length === 0 ? 'Add a booth type first.' : undefined}
                  onClick={() => setDialog({ kind: 'booth', booth: null })}
                >
                  <PlusIcon />
                  Add stand
                </Button>
              </>
            ) : null}
          </>
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the floor plan" />}

      {view === 'plan' ? (
        <InteractiveFloorPlan
          booths={data.items}
          types={types.items}
          canManage={canManage}
          venue={venue}
          zone={manifest?.tenant.timezone ?? 'UTC'}
          focusCode={search.booth}
          onEdit={onEdit}
          onMove={onMove}
        />
      ) : (
        <>
          <DataTable
            caption="Floor plan by status"
            title="Stands by status"
            columns={columns}
            data={summary}
            getRowId={(row) => row.status}
            empty={
              <EmptyState
                icon={<MapIcon />}
                title="No stands on the floor plan yet"
                description="The status of each stand shows here when the floor plan has stands."
              />
            }
          />

          <StandsTable
            booths={data.items}
            types={types.items}
            currency={currency}
            canManage={canManage}
            onEdit={onEdit}
            onMove={onMove}
          />
        </>
      )}

      {dialog?.kind === 'booth' ? (
        <BoothDialog booth={dialog.booth} types={types.items} onClose={close} />
      ) : null}

      {dialog?.kind === 'types' ? (
        <BoothTypesDialog types={types.items} currency={currency} onClose={close} />
      ) : null}

      <ConfirmDialog
        open={dialog?.kind === 'withdraw'}
        onOpenChange={(open) => (open ? undefined : close())}
        title={dialog?.kind === 'withdraw' ? `Withdraw stand ${dialog.booth.code}?` : ''}
        description="Exhibitors can no longer choose it. You can put it back on sale later."
        confirmLabel="Withdraw"
        destructive
        busy={setStatus.isPending}
        onConfirm={() => {
          if (dialog?.kind === 'withdraw') {
            setStatus.mutate({ id: dialog.booth.id, status: 'canceled' }, { onSuccess: close })
          }
        }}
      />
    </div>
  )
}
